import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Log, LogLevel, LogCategory, UserRole, User } from '@/types';
import { useLogger } from '@/contexts/LogContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { Loader2, Search, Download } from 'lucide-react';

interface SystemLogRealDataTableProps {
  currentUser: User;
}

const LogLevelBadge: React.FC<{ level: LogLevel }> = ({ level }) => {
    const badgeStyle = {
        [LogLevel.INFO]: 'bg-blue-500/20 text-blue-300',
        [LogLevel.WARN]: 'bg-yellow-500/20 text-yellow-300',
        [LogLevel.ERROR]: 'bg-red-500/20 text-red-300',
        [LogLevel.SUCCESS]: 'bg-green-500/20 text-green-300',
        [LogLevel.CRITICAL]: 'bg-purple-500/20 text-purple-300',
    }[level] || 'bg-gray-500/20 text-gray-300';

    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badgeStyle}`}>{level}</span>;
};

const LogCategoryBadge: React.FC<{ category: LogCategory }> = ({ category }) => {
    const categoryStyle = 'bg-gray-700 text-gray-300';
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${categoryStyle}`}>{category}</span>;
};

const SystemLogRealDataTable: React.FC<SystemLogRealDataTableProps> = ({ currentUser }) => {
    const { logs, isLoading: isLogsLoading } = useLogger();
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 15;

    const isAdmin = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SYSTEM_ADMIN_LIVE;

    const userVisibleLogs = useMemo(() => {
        if (isAdmin) return logs;
        return logs.filter(log => log.userId === currentUser?.id);
    }, [logs, isAdmin, currentUser]);

    const filteredLogs = useMemo(() => {
        return userVisibleLogs.filter(log =>
            log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.level && log.level.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (log.category && log.category.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [searchTerm, userVisibleLogs]);

    const paginatedLogs = useMemo(() => {
        const startIndex = (page - 1) * PAGE_SIZE;
        return filteredLogs.slice(startIndex, startIndex + PAGE_SIZE);
    }, [filteredLogs, page]);

    const hasMore = page * PAGE_SIZE < filteredLogs.length;

    const handleDownload = () => {
        const logsToDownload = isAdmin ? filteredLogs : filteredLogs.filter(log => log.userId === currentUser?.id);
        const csvContent = "data:text/csv;charset=utf-8,"
            + "Timestamp,Level,Category,Message\n"
            + logsToDownload.map((log: Log) => 
                `"${format(new Date(log.timestamp), 'Pp')}","${log.level}","${log.category}","${log.message.replace(/"/g, '""')}"`
            ).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "system_logs.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (isLogsLoading && logs.length === 0) return (
        <div className="flex justify-center items-center h-48">
            <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
            <p className="ml-4 text-kala-400">Loading system logs...</p>
        </div>
    );

    return (
        <div className="bg-kala-900 text-white rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">System Logs</h2>
                <div className="flex items-center space-x-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                        <input
                            type="text"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
                        />
                    </div>
                    <button 
                        onClick={handleDownload}
                        className="flex items-center px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Download Logs
                    </button>
                </div>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Level</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Message</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {paginatedLogs.map((log: Log) => (
                        <TableRow key={`${log.id}-${Math.random()}`}>
                            <TableCell>{format(new Date(log.timestamp), 'Pp')}</TableCell>
                            <TableCell><LogLevelBadge level={log.level} /></TableCell>
                            <TableCell><LogCategoryBadge category={log.category} /></TableCell>
                            <TableCell>{log.message}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className="flex justify-between items-center mt-6">
                <button 
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
                >
                    Previous
                </button>
                <span className="text-kala-400">Page {page}</span>
                <button 
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasMore}
                    className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default SystemLogRealDataTable;
